import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = {
  // Recipients
  getRecipients: () => axios.get(`${API_URL}/api/recipients`),
  createRecipient: (data) => axios.post(`${API_URL}/api/recipients`, data),
  updateRecipient: (id, data) => axios.put(`${API_URL}/api/recipients/${id}`, data),
  deleteRecipient: (id) => axios.delete(`${API_URL}/api/recipients/${id}`),

  // Recipient Invitations
  createInvitation: (data) => axios.post(`${API_URL}/api/recipients/invite`, data),
  getInvitations: () => axios.get(`${API_URL}/api/recipients/invitations`),
  deleteInvitation: (id) => axios.delete(`${API_URL}/api/recipients/invitations/${id}`),

  // Exchange
  getExchangeRate: () => axios.get(`${API_URL}/api/exchange/rate`),
  calculateExchange: (amount, currency = 'JPY') => axios.get(`${API_URL}/api/exchange/calculate?amount=${amount}&currency=${currency}`),

  // Email Verification
  verifyEmail: (data) => axios.post(`${API_URL}/api/auth/verify-email`, data),
  resendCode: (data) => axios.post(`${API_URL}/api/auth/resend-code`, data),

  // Transfers
  getTransfers: () => axios.get(`${API_URL}/api/transfers`),
  createTransfer: (data) => axios.post(`${API_URL}/api/transfers`, data),
  getTransfer: (id) => axios.get(`${API_URL}/api/transfers/${id}`),

  // KYC
  submitKYC: (data) => axios.post(`${API_URL}/api/kyc/submit`, data),
  getKYCStatus: () => axios.get(`${API_URL}/api/kyc/status`),

  // User Profile
  updateProfile: (data) => axios.put(`${API_URL}/api/users/profile`, data),
  uploadProfileImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/api/users/upload-profile-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getUserStats: () => axios.get(`${API_URL}/api/users/stats`),

  // Deposits
  createDeposit: (data) => axios.post(`${API_URL}/api/deposits`, data),
  getDeposits: () => axios.get(`${API_URL}/api/deposits`),
  getDeposit: (id) => axios.get(`${API_URL}/api/deposits/${id}`),

  // Admin
  getAdminDashboard: () => axios.get(`${API_URL}/api/admin/dashboard`),
  getAdminUsers: () => axios.get(`${API_URL}/api/admin/users`),
  getAdminUserDetail: (userId) => axios.get(`${API_URL}/api/admin/users/${userId}/detail`),
  createAdminUser: (data) => axios.post(`${API_URL}/api/admin/users`, data),
  updateUser: (userId, data) => axios.put(`${API_URL}/api/admin/users/${userId}`, data),
  deleteUser: (userId) => axios.delete(`${API_URL}/api/admin/users/${userId}`),
  adjustUserBalance: (userId, data) => axios.post(`${API_URL}/api/admin/users/${userId}/balance`, data),
  getAdminTransfers: () => axios.get(`${API_URL}/api/admin/transfers`),
  updateTransfer: (transferId, data) => axios.put(`${API_URL}/api/admin/transfers/${transferId}`, data),
  deleteTransfer: (transferId) => axios.delete(`${API_URL}/api/admin/transfers/${transferId}`),
  getAdminKYC: () => axios.get(`${API_URL}/api/admin/kyc`),
  updateKYC: (kycId, data) => axios.put(`${API_URL}/api/admin/kyc/${kycId}`, data),
  reviewKYC: (kycId, action, notes) => axios.post(`${API_URL}/api/admin/kyc/${kycId}/review`, null, {
    params: { action, notes }
  }),
  getAdminAML: () => axios.get(`${API_URL}/api/admin/aml`),
  getAdminAMLStats: () => axios.get(`${API_URL}/api/admin/aml/statistics`),
  getAdminAMLSettings: () => axios.get(`${API_URL}/api/admin/aml/settings`),
  updateAdminAMLSettings: (data) => axios.put(`${API_URL}/api/admin/aml/settings`, data),
  updateAMLFlagStatus: (flagId, status) => axios.post(`${API_URL}/api/admin/aml/${flagId}/status?status=${status}`),
  getAdminHighRiskUsers: () => axios.get(`${API_URL}/api/admin/aml/high-risk-users`),
  resolveAMLFlag: (flagId, data) => axios.post(`${API_URL}/api/admin/aml/${flagId}/resolve`, data),
  uploadAMLId: (flagId, side, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API_URL}/api/admin/aml/${flagId}/upload-id?side=${side}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAMLFlag: (flagId) => axios.delete(`${API_URL}/api/admin/aml/${flagId}`),
  getAdminDeposits: () => axios.get(`${API_URL}/api/admin/deposits`),
  createAdminDeposit: (userId, data) => axios.post(`${API_URL}/api/admin/users/${userId}/deposits`, data),
  deleteDeposit: (depositId) => axios.delete(`${API_URL}/api/admin/deposits/${depositId}`),
  confirmDeposit: (depositId, action) => axios.post(`${API_URL}/api/admin/deposits/${depositId}/confirm`, null, {
    params: { action }
  }),

  // Documents
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/api/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  submitIncomeCert: (data) => axios.post(`${API_URL}/api/documents/income`, data),
  updateIncomeCert: (data) => axios.put(`${API_URL}/api/documents/income`, data),
  getIncomeCert: () => axios.get(`${API_URL}/api/documents/income`),
  getIncomeCertHistory: () => axios.get(`${API_URL}/api/documents/income/history`),
  deleteIncomeCert: () => axios.delete(`${API_URL}/api/documents/income`),
  submitResidenceCard: (data) => axios.post(`${API_URL}/api/documents/residence`, data),
  updateResidenceCard: (data) => axios.put(`${API_URL}/api/documents/residence`, data),
  getResidenceCard: () => axios.get(`${API_URL}/api/documents/residence`),
  getResidenceCardHistory: () => axios.get(`${API_URL}/api/documents/residence/history`),
  deleteResidenceCard: () => axios.delete(`${API_URL}/api/documents/residence`),

  // Admin Documents
  getAdminIncomeCerts: () => axios.get(`${API_URL}/api/admin/documents/income`),
  updateAdminIncomeCert: (id, data) => axios.put(`${API_URL}/api/admin/documents/income/${id}`, data),
  reviewAdminIncomeCert: (id, action, notes) => axios.post(`${API_URL}/api/admin/documents/income/${id}/review`, null, { params: { action, notes } }),
  getAdminResidenceCards: () => axios.get(`${API_URL}/api/admin/documents/residence`),
  updateAdminResidenceCard: (id, data) => axios.put(`${API_URL}/api/admin/documents/residence/${id}`, data),
  reviewAdminResidenceCard: (id, action, notes) => axios.post(`${API_URL}/api/admin/documents/residence/${id}/review`, null, { params: { action, notes } }),

  // WebAuthn
  getWebAuthnRegisterOptions: () => axios.post(`${API_URL}/api/webauthn/register/options`),
  verifyWebAuthnRegister: (credential) => axios.post(`${API_URL}/api/webauthn/register/verify`, { credential }),
  getWebAuthnLoginOptions: () => axios.post(`${API_URL}/api/webauthn/login/options`),
  verifyWebAuthnLogin: (credential) => axios.post(`${API_URL}/api/webauthn/login/verify`, { credential }),
  getWebAuthnCredentials: () => axios.get(`${API_URL}/api/webauthn/credentials`),
  deleteWebAuthnCredential: (credId) => axios.delete(`${API_URL}/api/webauthn/credentials/${credId}`),

  // Admin Settings
  getFeeSettings: () => axios.get(`${API_URL}/api/admin/settings/fees`),
  updateFeeSettings: (data) => axios.put(`${API_URL}/api/admin/settings/fees`, data),

  // Referrals
  getReferralCode: () => axios.get(`${API_URL}/api/referrals/my-code`),
  getReferralStats: () => axios.get(`${API_URL}/api/referrals/stats`),
  getReferralList: () => axios.get(`${API_URL}/api/referrals/list`),
  getAdminReferrals: () => axios.get(`${API_URL}/api/admin/referrals`),
  getAdminReferralStats: () => axios.get(`${API_URL}/api/admin/referrals/stats`),
  getAdminReferralCodes: () => axios.get(`${API_URL}/api/admin/referrals/codes`),
  updateAdminReferralCode: (codeId, data) => axios.put(`${API_URL}/api/admin/referrals/codes/${codeId}`, data),
  deleteAdminReferralCode: (codeId) => axios.delete(`${API_URL}/api/admin/referrals/codes/${codeId}`),
  getAdminReferralSettings: () => axios.get(`${API_URL}/api/admin/settings/referrals`),
  updateAdminReferralSettings: (data) => axios.put(`${API_URL}/api/admin/settings/referrals`, data),
  getAdminEncryptionStatus: () => axios.get(`${API_URL}/api/admin/settings/encryption`),
};
